/**
 * 견적 문진 정본 — 네이티브 `QuoteQuestion.swift` 와 1:1로 맞춘다.
 *
 * ⚠️ 크로스레포 계약: questionId / optionId 값이 네이티브·서버(answers JSON)와 동일해야 한다.
 *    한쪽만 바꾸면 저장은 되는데 상대 화면에서 라벨이 안 붙는 형태로 조용히 어긋난다.
 *    라벨 표시용 축약 매핑은 constants/quoteCategories.js 의 describeAnswers 를 참고.
 */

/** 카테고리별 첫 질문("어떤 수업") 선택지 — 네이티브 QuoteCategory.all 과 동일.
 *  '취미무용(hobby)' 은 요청자 혼선 방지로 견적 요청에서 제외(기존 데이터 라벨은 quoteCategories 에 잔존). */
export const QUOTE_CLASS_OPTIONS = {
  ballet: [["adult", "성인 발레"], ["teen", "초중고 발레"], ["kid", "유아 발레"], ["major", "전공반"], ["etc", "기타"]],
  barre: [["adult", "성인 바레"], ["diet", "다이어트 바레"], ["posture", "체형교정·재활"], ["group", "그룹 바레"], ["etc", "기타"]],
  korean: [["adult", "성인 한국무용"], ["teen", "초중고"], ["kid", "유아"], ["major", "전공반(입시)"], ["etc", "기타"]],
  modern: [["adult", "성인 현대무용"], ["teen", "초중고"], ["major", "전공반(입시)"], ["contemp", "컨템포러리"], ["etc", "기타"]],
  practical: [["kpop", "방송댄스(K-pop)"], ["hiphop", "힙합"], ["cover", "커버댄스"], ["exam", "입시 실용무용"], ["etc", "기타"]],
  balletfit: [["adult", "성인 발레핏"], ["diet", "다이어트"], ["posture", "체형교정"], ["group", "그룹"], ["etc", "기타"]],
  musical: [["exam", "뮤지컬 입시(연기·노래·춤)"], ["audition", "오디션 준비"], ["hobby", "취미 뮤지컬"], ["workshop", "무대 워크숍"], ["etc", "기타"]],
};

/** 견적 요청 가능한 카테고리(취미무용 제외) */
export const QUOTE_REQUEST_CATEGORIES = Object.keys(QUOTE_CLASS_OPTIONS);

const opt = (pairs) => pairs.map(([value, text]) => ({ value, text }));

/** 카테고리 무관 공통 문진 — 네이티브 QuoteQuestion.common 과 순서까지 동일. */
export const QUOTE_COMMON_QUESTIONS = [
  { id: "age", type: "single", title: "레슨생의 연령대는 어떻게 되나요?", required: true,
    options: opt([["preschool", "미취학 아동"], ["elem", "초등학생"], ["middle", "중학생"], ["high", "고등학생"],
                  ["20s", "20대"], ["30s", "30대"], ["40s+", "40대 이상"]]) },
  { id: "format", type: "multi", title: "어떤 레슨 형태를 원하시나요?", required: true,
    options: opt([["private", "개인 레슨"], ["group", "그룹 레슨"], ["academy", "학원"], ["any", "무관"], ["etc", "기타"]]) },
  { id: "days", type: "multi", title: "선호하는 레슨 요일을 선택해 주세요.", required: true,
    options: opt([["mon", "월요일"], ["tue", "화요일"], ["wed", "수요일"], ["thu", "목요일"],
                  ["fri", "금요일"], ["sat", "토요일"], ["sun", "일요일"]]) },
  { id: "time", type: "multi", title: "희망하는 레슨 시간대는 언제인가요?", required: true,
    options: opt([["dawn", "이른 오전 (9시 이전)"], ["morning", "오전 (9~12시)"], ["noon", "오후 (12~3시)"],
                  ["afternoon", "늦은 오후 (3~6시)"], ["evening", "저녁 (6~9시)"], ["night", "늦은 저녁 (9시 이후)"]]) },
  { id: "gender", type: "single", title: "레슨생의 성별은 무엇인가요?", required: true,
    options: opt([["male", "남자"], ["female", "여자"]]) },
  { id: "teacherGender", type: "single", title: "선호하는 강사의 성별이 있나요?", required: false,
    options: opt([["any", "무관"], ["male", "남"], ["female", "여"]]) },
  { id: "when", type: "single", title: "레슨받고 싶은 날이 언제인가요?", required: true,
    options: opt([["negotiable", "협의 가능해요."], ["asap", "최대한 빨리 시작하고 싶어요."],
                  ["week", "일주일 안에 시작하고 싶어요."], ["date", "원하는 날짜가 있어요."], ["etc", "기타"]]) },
  { id: "region", type: "region", title: "레슨받고 싶은 지역이 어디인가요?", required: true, options: [] },
  { id: "method", type: "single", title: "레슨은 어디서 받고 싶으세요?", required: true,
    options: opt([["visitMe", "제가 있는 곳으로 와주세요."], ["visitThem", "강사가 있는 곳으로 갈게요."],
                  ["any", "어떤 방식이든 상관없어요."]]) },
  { id: "inquiry", type: "single", title: "수업 관련 문의사항을 알려 주세요!", required: true,
    options: opt([["later", "강사와 상담 시 논의할게요"], ["now", "지금 작성할게요"]]) },
  { id: "detail", type: "text", title: "추가 요청사항이 있으면 알려 주세요.", required: false, options: [] },
];

/** 카테고리 → 전체 문진(첫 질문 + 공통). 질문·선택지 값은 네이티브 questions(for:) 와 동일. */
export function quoteQuestionsFor(categoryId) {
  const classOptions = QUOTE_CLASS_OPTIONS[categoryId] || QUOTE_CLASS_OPTIONS.ballet;
  return [
    { id: "class", type: "single", title: "어떤 수업을 원하시나요?", required: true, options: opt(classOptions) },
    ...QUOTE_COMMON_QUESTIONS,
  ];
}
